import { c as _c } from "react/compiler-runtime";
import { Box, CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { memo } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { cn } from '@/lib/utils';
import type { ChatLayout, ToolSessionSummary } from './types';
interface ToolSidebarProperties {
  className?: string;
  layout: ChatLayout;
  sessions: ToolSessionSummary[];
}
const spinnerIcon = <CuteSpinner size={14} className="border-current/40 border-t-current" />;
const stateIconMap: Record<string, ReactNode> = {
  call: spinnerIcon,
  'partial-call': spinnerIcon,
  result: <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
};
export const ToolSidebar = memo(function ToolSidebarComponent(t0) {
  const $ = _c(10);
  const {
    layout,
    sessions,
    className
  } = t0;
  if (layout !== "floating" || sessions.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("hidden w-72 shrink-0 flex-col border-l border-border/40 bg-card/60 p-4 text-sm text-muted-foreground lg:flex", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70"><Box className="h-4 w-4" /><span>Tool activity</span></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== sessions) {
    t3 = sessions.map(_temp);
    $[3] = sessions;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="space-y-2">{t3}</div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <aside className={t1}>{t2}{t4}</aside>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
});
function formatResultPreview(result: unknown): string {
  if (result === null || result === undefined) {
    return '';
  }
  if (typeof result === 'string') {
    return result.slice(0, 120);
  }
  if (typeof result === 'number' || typeof result === 'boolean') {
    return String(result).slice(0, 120);
  }
  try {
    return JSON.stringify(result).slice(0, 120);
  } catch {
    return '[unserializable result]';
  }
}
function _temp(session) {
  const icon = stateIconMap[session.state ?? "call"] ?? spinnerIcon;
  return <div key={session.toolCallId} className="flex items-start gap-2 rounded-xl border border-border/30 bg-background/60 p-3 text-xs shadow-sm"><div className="mt-0.5">{icon}</div><div className="min-w-0"><p className="font-medium text-card-foreground">{session.toolName}</p>{session.result ? <p className="truncate text-[11px] text-muted-foreground/80">{formatResultPreview(session.result)}</p> : <p className="text-[11px] uppercase tracking-wide text-muted-foreground/60">Running…</p>}</div></div>;
}